import React, { useState } from 'react'
import { Settings, Package, Container, Database, Cog } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { cn } from '@wove/web-ui'
import { useIntegrations } from '@wove/api'
import CustomFields from './CustomFields'
import UnitTypes from './UnitTypes'
import ContainerTypes from './ContainerTypes'
import CustomFieldMappings from './CustomFieldMappings'
import IntegrationSettings from './IntegrationSettings'

const getTabs = (integrationId: string) => [
  {
    id: 'integration-settings',
    label: 'Integration Settings',
    icon: <Cog className="h-4 w-4" />,
    component: () => <IntegrationSettings integrationId={integrationId} />,
  },
  {
    id: 'custom-fields',
    label: 'Custom Fields',
    icon: <Settings className="h-4 w-4" />,
    component: () => <CustomFields integrationId={integrationId} />,
  },
  {
    id: 'custom-field-mappings',
    label: 'Field Mappings',
    icon: <Database className="h-4 w-4" />,
    component: () => <CustomFieldMappings integrationId={integrationId} />,
  },
  {
    id: 'unit-types',
    label: 'Unit Types',
    icon: <Package className="h-4 w-4" />,
    component: () => <UnitTypes integrationId={integrationId} />,
  },
  {
    id: 'container-types',
    label: 'Container Types',
    icon: <Container className="h-4 w-4" />,
    component: () => <ContainerTypes integrationId={integrationId} />,
  },
]

export default function IntegrationDetailPage() {
  const { integrationId } = useParams<{ integrationId: string }>()
  const [activeTab, setActiveTab] = useState('integration-settings')
  const { integrations, isLoading, error } = useIntegrations()

  if (error) {
    return (
      <div className="p-6 max-w-6xl">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error Loading Integration
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Unable to load integration details. Please try refreshing the page.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading integration...</span>
        </div>
      </div>
    )
  }

  const integration = integrations.find(i => i.id === integrationId)
  
  if (!integration) {
    return (
      <div className="p-6 max-w-6xl">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Integration Not Found
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>The integration with ID "{integrationId}" could not be found.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const tabs = getTabs(integrationId!)
  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || (() => <IntegrationSettings integrationId={integrationId!} />)

  return (
    <div className="p-6 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{integration.name}</h1>
        <p className="text-lg text-gray-600">
          {integration.integrationType} • ID: {integration.id}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white">
        <ActiveComponent />
      </div>
    </div>
  )
}